import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { toast } from 'react-toastify';
import { Campaign } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import NotificationRecipientFields from '../coverage/NotificationRecipientFields';
import {
  channelUsesEmail,
  channelUsesSms,
  channelUsesWhatsApp,
  NOTIFICATION_CHANNEL_OPTIONS,
  type NotificationChannelSelection,
} from '../../utils/notificationChannels';
import ReportScheduleRecurrenceFields, {
  formatRecurrenceSummary,
  type ReportScheduleRecurrenceValue,
} from './ReportScheduleRecurrenceFields';
import {
  DAY_OF_MONTH_OPTIONS,
  getScheduledReportLabel,
  isDailyStaffingAlertReport,
  isPdfScheduledReport,
  reportRequiresDateRange,
  reportUsesIsoWeek,
  SCHEDULED_REPORT_TYPES,
} from '../../constants/scheduledReportTypes';

const emptyForm = {
  reportType: 'daily-summary',
  scheduleTime: '08:00',
  timezone: 'America/Chicago',
  emailRecipients: '',
  smsRecipients: '',
  channel: 'email' as NotificationChannelSelection,
  campaignIds: [] as string[],
  periodStartDay: 1,
  periodEndDay: -1,
  frequencyType: 'week' as ReportScheduleRecurrenceValue['frequencyType'],
  frequencyInterval: 1,
  frequencyDayOfMonth: 1,
  daysOfWeek: [1] as number[],
  stopDate: '',
  isEnabled: false,
  includeCampaignSummary: true,
  includeUncoveredPositions: true,
  includeCoveredAbsences: true,
  notes: '',
};

function expandCommaSeparated(values: string[]): string[] {
  return values.flatMap((value) => (
    value.includes(',')
      ? value.split(',').map((part) => part.trim()).filter(Boolean)
      : [value]
  ));
}

function parseRecipientList(value?: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return expandCommaSeparated(parsed.map((item) => String(item)).filter(Boolean));
    }
  } catch {
    return expandCommaSeparated(value.split(',').map((part) => part.trim()).filter(Boolean));
  }
  return [];
}

function parseJsonArray(value?: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function parseNumberArray(value?: string | null): number[] {
  return parseJsonArray(value).map((item) => Number(item)).filter((item) => Number.isFinite(item));
}

function loadCampaignIdsFromSchedule(schedule: any): string[] {
  const ids = parseJsonArray(schedule.campaignIds);
  if (ids.length > 0) return ids;
  return schedule.campaignId ? [schedule.campaignId] : [];
}

function normalizeFrequencyType(raw?: string): ReportScheduleRecurrenceValue['frequencyType'] {
  if (raw === 'daily') return 'day';
  if (raw === 'weekly') return 'week';
  if (raw === 'monthly') return 'month';
  if (raw === 'day' || raw === 'week' || raw === 'month') return raw;
  return 'week';
}

function buildRecurrenceFromSchedule(schedule: any): ReportScheduleRecurrenceValue {
  return {
    frequencyType: normalizeFrequencyType(schedule.frequencyType),
    frequencyInterval: Number(schedule.frequencyInterval ?? 1) || 1,
    frequencyDayOfMonth: Number(schedule.frequencyDayOfMonth ?? 1),
    daysOfWeek: parseNumberArray(schedule.daysOfWeek),
    stopDate: schedule.stopDate || '',
  };
}

function formatScheduleFrequency(schedule: any): string {
  return formatRecurrenceSummary(buildRecurrenceFromSchedule(schedule));
}

function formatSchedulePeriod(schedule: any): string {
  if (reportUsesIsoWeek(schedule.reportType)) return 'Calendar week';
  if (!reportRequiresDateRange(schedule.reportType)) return 'Run date';
  const start = Number(schedule.periodStartDay ?? 1);
  const end = Number(schedule.periodEndDay ?? -1);
  const startLabel = start === -1 ? 'Last' : String(start);
  const endLabel = end === -1 ? 'Last' : String(end);
  return `Day ${startLabel} – ${endLabel}`;
}

function loadRecipientsFromSchedule(schedule: any): { emailRecipients: string; smsRecipients: string } {
  const channel = schedule.channel as NotificationChannelSelection;
  const storedEmails = parseRecipientList(schedule.recipients);
  const storedSms = parseRecipientList(schedule.smsRecipients);

  if (storedSms.length > 0 || storedEmails.some((r) => r.includes('@'))) {
    return {
      emailRecipients: channelUsesEmail(channel) ? storedEmails.join(', ') : '',
      smsRecipients: (channelUsesSms(channel) || channelUsesWhatsApp(channel)) ? storedSms.join(', ') : '',
    };
  }

  if (channel === 'email') {
    return { emailRecipients: storedEmails.join(', '), smsRecipients: '' };
  }
  if (channel === 'sms' || channel === 'whatsapp') {
    return { emailRecipients: '', smsRecipients: storedEmails.join(', ') };
  }

  return {
    emailRecipients: storedEmails.filter((r) => r.includes('@')).join(', '),
    smsRecipients: storedEmails.filter((r) => !r.includes('@')).join(', '),
  };
}

interface ScheduledReportsTabProps {
  canManage: boolean;
  campaigns: Campaign[];
}

const ScheduledReportsTab: React.FC<ScheduledReportsTabProps> = ({ canManage, campaigns }) => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<any>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [defaultPhone, setDefaultPhone] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const response = await apiClient.operations.getReportSchedules();
      setSchedules(response.data.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    apiClient.userNotificationPreferences.get()
      .then((res) => setDefaultPhone(res.data?.data?.phoneNumber || ''))
      .catch(() => setDefaultPhone(''));
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      emailRecipients: user?.email || '',
      smsRecipients: defaultPhone || '',
    });
    setDialogOpen(true);
  };

  const openEdit = (schedule: any) => {
    const { emailRecipients, smsRecipients } = loadRecipientsFromSchedule(schedule);
    setEditingId(schedule.id);
    setForm({
      reportType: schedule.reportType,
      scheduleTime: schedule.scheduleTime,
      timezone: schedule.timezone || 'America/Chicago',
      emailRecipients,
      smsRecipients,
      channel: schedule.channel,
      campaignIds: loadCampaignIdsFromSchedule(schedule),
      periodStartDay: Number(schedule.periodStartDay ?? 1),
      periodEndDay: Number(schedule.periodEndDay ?? -1),
      ...buildRecurrenceFromSchedule(schedule),
      isEnabled: !!schedule.isEnabled,
      includeCampaignSummary: schedule.includeCampaignSummary !== false,
      includeUncoveredPositions: schedule.includeUncoveredPositions !== false,
      includeCoveredAbsences: schedule.includeCoveredAbsences !== false,
      notes: schedule.notes || '',
    });
    setDialogOpen(true);
  };

  const splitRecipients = (value: string) => value.split(',').map((r) => r.trim()).filter(Boolean);

  const save = async () => {
    const emailList = splitRecipients(form.emailRecipients);
    const smsList = splitRecipients(form.smsRecipients);
    const pdfReport = isPdfScheduledReport(form.reportType);
    const channel = pdfReport ? 'email' : form.channel;

    if (channelUsesEmail(channel) && emailList.length === 0) {
      toast.error('Select or enter at least one email recipient');
      return;
    }
    if (!pdfReport && (channelUsesSms(channel) || channelUsesWhatsApp(channel)) && smsList.length === 0) {
      toast.error('Select or enter a phone number');
      return;
    }
    if (form.frequencyType === 'week' && form.daysOfWeek.length === 0) {
      toast.error('Select at least one day of the week');
      return;
    }

    try {
      const payload = {
        reportType: form.reportType,
        scheduleTime: form.scheduleTime,
        timezone: form.timezone,
        channel,
        campaignIds: form.campaignIds,
        periodStartDay: form.periodStartDay,
        periodEndDay: form.periodEndDay,
        frequencyType: form.frequencyType,
        frequencyInterval: form.frequencyInterval,
        frequencyDayOfMonth: form.frequencyDayOfMonth,
        daysOfWeek: form.daysOfWeek,
        stopDate: form.stopDate || null,
        isEnabled: form.isEnabled,
        includeCampaignSummary: form.includeCampaignSummary,
        includeUncoveredPositions: form.includeUncoveredPositions,
        includeCoveredAbsences: form.includeCoveredAbsences,
        notes: form.notes,
        recipients: emailList,
        smsRecipients: smsList,
      };
      if (editingId) {
        await apiClient.operations.updateReportSchedule(editingId, payload);
        toast.success('Schedule updated');
      } else {
        await apiClient.operations.createReportSchedule(payload);
        toast.success('Schedule created');
      }
      setDialogOpen(false);
      await load();
    } catch (error: any) {
      const message = error?.response?.data?.message
        || error?.message
        || (error?.response?.status ? `Request failed (${error.response.status})` : 'Failed to save schedule');
      toast.error(message);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await apiClient.operations.deleteReportSchedule(id);
      toast.success('Schedule deleted');
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete schedule');
    }
  };

  const testNow = async (id: string) => {
    try {
      await apiClient.operations.testReportSchedule(id);
      toast.success('Test report sent');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Test report failed');
    }
  };

  const isAlertReport = isDailyStaffingAlertReport(form.reportType);
  const isPdfReport = isPdfScheduledReport(form.reportType);
  const showPeriodRange = reportRequiresDateRange(form.reportType);
  const showIsoWeekNote = reportUsesIsoWeek(form.reportType);
  const showSingleDateNote = !showPeriodRange && !showIsoWeekNote;
  const isKnownReportType = SCHEDULED_REPORT_TYPES.some((t) => t.value === form.reportType);
  const timeReportOptions = SCHEDULED_REPORT_TYPES.filter((t) => t.group === 'time-reports');
  const alertReportOptions = SCHEDULED_REPORT_TYPES.filter((t) => t.group === 'alerts');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Scheduled Reports</Typography>
        {canManage && <Button variant="contained" onClick={openCreate}>Create Schedule</Button>}
      </Box>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have view-only access to scheduled reports.
        </Alert>
      )}

      {schedules.length === 0 && !loading ? (
        <Alert severity="info">No scheduled reports configured yet.</Alert>
      ) : (
        <TableContainer
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
            '& .MuiTable-root': { minWidth: 1100 },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Report', 'Time', 'Frequency', 'Period', 'Campaigns', 'Channel', 'Enabled', 'Email', 'SMS', 'Stop', 'Actions'].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => {
                const { emailRecipients, smsRecipients } = loadRecipientsFromSchedule(schedule);
                const campaignIds = loadCampaignIdsFromSchedule(schedule);
                const campaignLabel = campaignIds.length === 0
                  ? 'All'
                  : campaignIds.map((id) => campaigns.find((c) => c.id === id)?.name || id.slice(0, 8)).join(', ');
                return (
                  <TableRow key={schedule.id}>
                    <TableCell>{getScheduledReportLabel(schedule.reportType)}</TableCell>
                    <TableCell>{schedule.scheduleTime}</TableCell>
                    <TableCell>{formatScheduleFrequency(schedule)}</TableCell>
                    <TableCell>{formatSchedulePeriod(schedule)}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{campaignLabel}</TableCell>
                    <TableCell>{schedule.channel}</TableCell>
                    <TableCell>{schedule.isEnabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{emailRecipients || '—'}</TableCell>
                    <TableCell>{smsRecipients || '—'}</TableCell>
                    <TableCell>{schedule.stopDate || '—'}</TableCell>
                    <TableCell>
                      {canManage && (
                        <>
                          <IconButton size="small" onClick={() => openEdit(schedule)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => testNow(schedule.id)}><PlayArrowIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => remove(schedule.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={form.reportType}
                  label="Report Type"
                  onChange={(e) => {
                    const nextType = e.target.value;
                    const pdfReport = isPdfScheduledReport(nextType);
                    setForm({
                      ...form,
                      reportType: nextType,
                      channel: pdfReport ? 'email' : form.channel,
                    });
                  }}
                >
                  <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem' }}>
                    Time Reports (PDF email)
                  </MenuItem>
                  {timeReportOptions.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                  <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem' }}>
                    Alerts
                  </MenuItem>
                  {alertReportOptions.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                  {!isKnownReportType && form.reportType && (
                    <MenuItem value={form.reportType}>
                      {getScheduledReportLabel(form.reportType)} (legacy)
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              {SCHEDULED_REPORT_TYPES.find((t) => t.value === form.reportType)?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {SCHEDULED_REPORT_TYPES.find((t) => t.value === form.reportType)?.description}
                  {isPdfReport ? ' Delivered as a PDF email attachment.' : ''}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="time" label="Time" value={form.scheduleTime} onChange={(e) => setForm({ ...form, scheduleTime: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <ReportScheduleRecurrenceFields
                value={{
                  frequencyType: form.frequencyType,
                  frequencyInterval: form.frequencyInterval,
                  frequencyDayOfMonth: form.frequencyDayOfMonth,
                  daysOfWeek: form.daysOfWeek,
                  stopDate: form.stopDate,
                }}
                onChange={(recurrence) => setForm({ ...form, ...recurrence })}
                onClear={() => setForm({ ...form, stopDate: '' })}
              />
            </Grid>
            {showPeriodRange && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Report period (day of month)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uses the run month. Example: day 1 to last day = full calendar month.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>From day</InputLabel>
                    <Select
                      value={form.periodStartDay}
                      label="From day"
                      onChange={(e) => setForm({ ...form, periodStartDay: Number(e.target.value) })}
                    >
                      {DAY_OF_MONTH_OPTIONS.map((option) => (
                        <MenuItem key={`start-${option.value}`} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>To day</InputLabel>
                    <Select
                      value={form.periodEndDay}
                      label="To day"
                      onChange={(e) => setForm({ ...form, periodEndDay: Number(e.target.value) })}
                    >
                      {DAY_OF_MONTH_OPTIONS.map((option) => (
                        <MenuItem key={`end-${option.value}`} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {showIsoWeekNote && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  This report uses the calendar week (Mon–Sun) that contains each run date.
                </Alert>
              </Grid>
            )}
            {showSingleDateNote && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  This report uses the run date only (current day when the schedule fires).
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Campaigns</InputLabel>
                <Select
                  multiple
                  value={form.campaignIds}
                  label="Campaigns"
                  onChange={(e) => {
                    const raw = e.target.value;
                    const value = typeof raw === 'string' ? raw.split(',') : (raw as string[]);
                    if (value.includes('__all__')) {
                      const allSelected = campaigns.length > 0
                        && form.campaignIds.length === campaigns.length;
                      setForm({
                        ...form,
                        // All → select every campaign; Exclude all → clear selection
                        campaignIds: allSelected ? [] : campaigns.map((c) => c.id),
                      });
                      return;
                    }
                    setForm({
                      ...form,
                      campaignIds: value.filter((id) => id !== '__all__'),
                    });
                  }}
                  renderValue={(selected: string[]) => {
                    if (selected.length === 0) return 'All campaigns';
                    if (campaigns.length > 0 && selected.length === campaigns.length) {
                      return `All campaigns (${campaigns.length})`;
                    }
                    return selected
                      .map((id: string) => campaigns.find((c) => c.id === id)?.name || id)
                      .join(', ');
                  }}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 360 } },
                    autoFocus: false,
                  }}
                >
                  <MenuItem
                    value="__all__"
                    dense
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      bgcolor: 'background.paper',
                      zIndex: 1,
                    }}
                  >
                    <Checkbox
                      checked={campaigns.length > 0 && form.campaignIds.length === campaigns.length}
                      indeterminate={
                        form.campaignIds.length > 0
                        && form.campaignIds.length < campaigns.length
                      }
                    />
                    <ListItemText
                      primary={
                        campaigns.length > 0 && form.campaignIds.length === campaigns.length
                          ? 'Exclude all'
                          : 'All'
                      }
                      secondary={
                        campaigns.length > 0 && form.campaignIds.length === campaigns.length
                          ? 'Clear every campaign'
                          : 'Select every campaign'
                      }
                    />
                  </MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      <Checkbox checked={form.campaignIds.includes(campaign.id)} />
                      <ListItemText primary={campaign.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Use All / Exclude all at the top of the list. Empty selection also means all campaigns.
                Multiple selections send one PDF per campaign.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.isEnabled} onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })} />} label="Enabled" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={isPdfReport ? 'email' : form.channel}
                  label="Channel"
                  disabled={isPdfReport}
                  onChange={(e) => setForm({ ...form, channel: e.target.value as NotificationChannelSelection })}
                >
                  {(isPdfReport
                    ? NOTIFICATION_CHANNEL_OPTIONS.filter((option) => option.value === 'email')
                    : NOTIFICATION_CHANNEL_OPTIONS
                  ).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isPdfReport && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Time Reports are sent as PDF attachments via email only.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <NotificationRecipientFields
                channel={isPdfReport ? 'email' : form.channel}
                emailRecipients={form.emailRecipients}
                smsRecipients={form.smsRecipients}
                onEmailRecipientsChange={(value) => setForm({ ...form, emailRecipients: value })}
                onSmsRecipientsChange={(value) => setForm({ ...form, smsRecipients: value })}
                defaultEmail={user?.email}
                defaultPhone={defaultPhone}
              />
            </Grid>
            {!isAlertReport && !isPdfReport && (
              <Grid item xs={12}>
                <FormControlLabel control={<Checkbox checked={form.includeCampaignSummary} onChange={(e) => setForm({ ...form, includeCampaignSummary: e.target.checked })} />} label="Include campaign summary" />
                <FormControlLabel control={<Checkbox checked={form.includeUncoveredPositions} onChange={(e) => setForm({ ...form, includeUncoveredPositions: e.target.checked })} />} label="Include uncovered positions" />
                <FormControlLabel control={<Checkbox checked={form.includeCoveredAbsences} onChange={(e) => setForm({ ...form, includeCoveredAbsences: e.target.checked })} />} label="Include covered absences" />
              </Grid>
            )}
            {!isAlertReport && !isPdfReport && (
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Optional message" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Grid>
            )}
            {isPdfReport && (
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Optional email message" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} helperText="Included in the email body above the PDF attachment." />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledReportsTab;
